import { b64urlEncode, b64urlDecode } from './base64url';
// Note: In test (Jest) or older Node runtimes, global.crypto.subtle may be missing.
// We provide a seamless fallback to Node's createHmac for signing / verifying.
// This keeps production (WebCrypto) path while ensuring tests pass without polyfills.
let nodeCrypto: typeof import('crypto') | undefined;
try { nodeCrypto = require('crypto'); } catch { /* ignore for browser */ }

// HS256-style signed session token (no external libs)
// Format: v1.<body>.<sig>
// body: base64url(JSON string)

const enc = new TextEncoder();

function getSecret(): Uint8Array {
  const secret = process.env.SESSION_HMAC_SECRET;
  if (!secret) throw new Error('SESSION_HMAC_SECRET not set');
  // Allow either raw string (>=32 chars) or base64url encoded key
  if (/^[A-Za-z0-9-_]+$/.test(secret) && (secret.length % 4 !== 1)) {
    try {
      const decoded = b64urlDecode(secret);
      if (decoded.byteLength >= 32) return decoded;
    } catch { /* fall through */ }
  }
  if (secret.length < 32) throw new Error('SESSION_HMAC_SECRET too short (<32 chars)');
  return enc.encode(secret);
}

async function subtleImportKey() {
  const raw = getSecret();
  const buf = raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer;
  return crypto.subtle.importKey('raw', buf, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign','verify']);
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

async function signBody(body: string): Promise<string> {
  // Prefer WebCrypto if available
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const key = await subtleImportKey();
      const bodyBytes = enc.encode(body);
      const bodyBuf = bodyBytes.buffer.slice(bodyBytes.byteOffset, bodyBytes.byteOffset + bodyBytes.byteLength) as ArrayBuffer;
      const sigBytes = new Uint8Array(await crypto.subtle.sign('HMAC', key, bodyBuf));
      return b64urlEncode(sigBytes);
    } catch { /* fall back */ }
  }
  if (!nodeCrypto) throw new Error('No crypto implementation available');
  const h = nodeCrypto.createHmac('sha256', getSecret());
  h.update(body);
  return b64urlEncode(new Uint8Array(h.digest()));
}

async function verifyBody(body: string, sig: string): Promise<void> {
  const expected = await signBody(body);
  // constant time compare
  const a = b64urlDecode(expected);
  const b = b64urlDecode(sig);
  if (!timingSafeEqual(a, b)) throw new Error('signature');
}

export interface SessionClaims {
  sub: string | number;
  alias?: string;
  ver: number; // schema version
  iat?: number;
  exp?: number;
  [k: string]: any;
}

export async function createSessionToken(claims: Omit<SessionClaims,'iat'|'exp'>, ttlSeconds = 3600): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const full: SessionClaims = { sub: claims.sub, ver: claims.ver, ...claims, iat: now, exp: now + ttlSeconds };
  const json = JSON.stringify(full);
  const body = b64urlEncode(enc.encode(json));
  const sig = await signBody(body);
  return `v1.${body}.${sig}`;
}

export async function verifySessionToken(token: string): Promise<SessionClaims> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('format');
  const [v, body, sig] = parts;
  if (v !== 'v1') throw new Error('version');
  await verifyBody(body, sig);
  const json = JSON.parse(new TextDecoder().decode(b64urlDecode(body)));
  if (typeof json.exp !== 'number' || json.exp < Math.floor(Date.now() / 1000)) throw new Error('expired');
  return json as SessionClaims;
}
